import React from "react";
import { PlayerState } from "../../../../core/protocol/types";
import { TEAM_NAMES, TEAM_COLORS } from "../../../../core/constants";
import "../../ResultScreen.css";

type SortColumn = "frags" | "deaths" | "score" | "lines";

interface TeamStats {
  frags: number;
  deaths: number;
  score: number;
  lines: number;
  players: number;
  roundWins?: number;
}

interface RankTableProps {
  players: PlayerState[];
  playersByTeam: Map<number, PlayerState[]>;
  activeTeams: Array<[number, TeamStats]>;
  activeSort: SortColumn;
  onSort: (column: SortColumn) => void;
  localConnectionId: string | null;
  localNumPlayer?: number;
  localTeam: number;
  isSinglePlayer: boolean;
}

const RankTable: React.FC<RankTableProps> = ({
  players,
  playersByTeam,
  activeTeams,
  activeSort,
  onSort,
  localConnectionId,
  localNumPlayer,
  localTeam,
  isSinglePlayer,
}) => {
  const getStatValue = (player: PlayerState, column: SortColumn): number => {
    return player[column] || 0;
  };

  const formatStat = (value: number): string => {
    return value.toLocaleString();
  };

  return (
    <div className="result-screen-table-container">
      <table className="result-screen-table">
        <thead>
          <tr>
            <th className="result-col-player">Player</th>
            <th
              className={`result-col-stat sortable ${activeSort === "frags" ? "active" : ""}`}
              onClick={() => onSort("frags")}
            >
              Frags
              {activeSort === "frags" && <span className="sort-indicator">▼</span>}
            </th>
            <th
              className={`result-col-stat sortable ${activeSort === "deaths" ? "active" : ""}`}
              onClick={() => onSort("deaths")}
            >
              Deaths
              {activeSort === "deaths" && <span className="sort-indicator">▼</span>}
            </th>
            <th
              className={`result-col-stat sortable ${activeSort === "score" ? "active" : ""}`}
              onClick={() => onSort("score")}
            >
              Score
              {activeSort === "score" && <span className="sort-indicator">▼</span>}
            </th>
            <th
              className={`result-col-stat sortable ${activeSort === "lines" ? "active" : ""}`}
              onClick={() => onSort("lines")}
            >
              Lines
              {activeSort === "lines" && <span className="sort-indicator">▼</span>}
            </th>
          </tr>
        </thead>
        <tbody>
          {activeTeams.map(([teamId, teamStatsData]) => {
            // Get players for this team (already sorted globally)
            const teamPlayers = playersByTeam.get(teamId) || [];
            const teamColor = TEAM_COLORS[teamId];
            const isMyTeam = localTeam === teamId;

            return (
              <React.Fragment key={teamId}>
                {/* Display players in global sort order (matches original score.order[]) */}
                {teamPlayers.map((player) => {
                  // Use numPlayer for consistency with game logic (matches original design)
                  const isLocalPlayer = localNumPlayer !== undefined && player.numPlayer === localNumPlayer;
                  return (
                    <tr
                      key={player.connectionId}
                      className={`result-screen-player-row ${
                        isLocalPlayer ? "local-player" : ""
                      } ${isMyTeam ? "my-team" : ""}`}
                    >
                      <td className="result-col-player">
                        <span
                          className="result-screen-player-name"
                          style={{
                            color: teamColor,
                            opacity: player.isGone ? 0.6 : 1,
                          }}
                        >
                          {player.name}
                          {player.isGone ? " *" : ""}
                          {isLocalPlayer && (
                            <span className="result-screen-you-badge">You</span>
                          )}
                        </span>
                      </td>
                      <td className="result-col-stat">
                        {formatStat(getStatValue(player, "frags"))}
                      </td>
                      <td className="result-col-stat">
                        {formatStat(getStatValue(player, "deaths"))}
                      </td>
                      <td className="result-col-stat">
                        {formatStat(getStatValue(player, "score"))}
                      </td>
                      <td className="result-col-stat">
                        {formatStat(getStatValue(player, "lines"))}
                      </td>
                    </tr>
                  );
                })}
                {teamStatsData.players > 1 && (
                  <tr className="result-screen-team-total">
                    <td className="result-col-player">
                      <span style={{ color: teamColor, fontWeight: "bold" }}>
                        {TEAM_NAMES[teamId]} Total
                      </span>
                    </td>
                    <td className="result-col-stat">
                      {formatStat(teamStatsData.frags)}
                    </td>
                    <td className="result-col-stat">
                      {formatStat(teamStatsData.deaths)}
                    </td>
                    <td className="result-col-stat">
                      {formatStat(teamStatsData.score)}
                    </td>
                    <td className="result-col-stat">
                      {formatStat(teamStatsData.lines)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RankTable;

